<template>
  <div class="dashboard-page">
    <div class="menu-title">数据看板</div>
    <div class="menu-content dashboard-content">
      <div class="dashboard-panel">
        <div class="status-card">
          <div class="card-title">状态</div>
          <div class="card-row">
            <span class="card-label">已拥有</span>
            <span class="card-number">{{ stats.totalUsers }}</span>
            <span class="card-label">名用户</span>
          </div>
          <div class="card-row">
            <span class="card-label">昨日新增</span>
            <span class="card-number highlight">{{ stats.newUsers }}</span>
            <span class="card-label">名用户</span>
          </div>
          <el-button type="info" plain class="manage-btn" @click="goUserManage">用户管理</el-button>
        </div>
        <div class="chart-card">
          <div class="chart-header">
            <div class="chart-tabs">
              <span class="tab-item" :class="{ active: activeTab === '7' }" @click="switchTab('7')">最近7天</span>
              <span class="tab-item" :class="{ active: activeTab === '15' }" @click="switchTab('15')">最近15天</span>
              <span class="tab-item" :class="{ active: activeTab === '30' }" @click="switchTab('30')">最近30天</span>
            </div>
            <div class="chart-legend">
              <span class="legend-item">
                <span class="legend-dot visit"></span>访问量
              </span>
              <span class="legend-item">
                <span class="legend-dot activate"></span>用户量
              </span>
            </div>
          </div>
          <div ref="chart" class="chart-container"></div>
        </div>
      </div>

      <section class="usage-section" v-loading="usageLoading">
        <div class="usage-header">
          <div>
            <h2>全部用户</h2>
            <p>{{ usageStatistics.startDate || '-' }} 至 {{ usageStatistics.endDate || '-' }}</p>
          </div>
          <div class="usage-filter">
            <el-radio-group
                v-model="usageMetric"
                class="usage-metric-switch"
                size="small"
                @change="renderUsageCharts">
              <el-radio-button label="quota">总 Token</el-radio-button>
              <el-radio-button label="input">输入 Token</el-radio-button>
              <el-radio-button label="output">输出 Token</el-radio-button>
              <el-radio-button label="price">价格</el-radio-button>
            </el-radio-group>
            <el-date-picker
                v-model="usageQuery.dateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                :clearable="false">
            </el-date-picker>
            <el-button type="primary" icon="el-icon-search" @click="queryUsage">查询</el-button>
          </div>
        </div>

        <div class="usage-summary">
          <div class="usage-summary-item">
            <span>记忆圈累计</span>
            <strong>{{ formatQuotaPrice(usageStatistics.memoryCircle.totalQuota) }}</strong>
            <small>总 Token {{ formatToken(usageStatistics.memoryCircle.totalQuota) }}</small>
            <small>输入 Token {{ formatToken(usageStatistics.memoryCircle.inputTokenTotal) }}</small>
            <small>输出 Token {{ formatToken(usageStatistics.memoryCircle.outputTokenTotal) }}</small>
          </div>
          <div class="usage-summary-item">
            <span>对话抽取累计</span>
            <strong>{{ formatQuotaPrice(usageStatistics.conversation.totalQuota) }}</strong>
            <small>总 Token {{ formatToken(usageStatistics.conversation.totalQuota) }}</small>
            <small>输入 Token {{ formatToken(usageStatistics.conversation.inputTokenTotal) }}</small>
            <small>输出 Token {{ formatToken(usageStatistics.conversation.outputTokenTotal) }}</small>
          </div>
          <div class="usage-summary-item overall">
            <span>全部累计</span>
            <strong>{{ formatQuotaPrice(usageStatistics.totalQuota) }}</strong>
            <small>总 Token {{ formatToken(usageStatistics.totalQuota) }}</small>
            <small>输入 Token {{ formatToken(usageStatistics.inputTokenTotal) }}</small>
            <small>输出 Token {{ formatToken(usageStatistics.outputTokenTotal) }}</small>
          </div>
        </div>

        <div class="usage-group">
          <div class="usage-group-title">
            <span>记忆圈</span>
            <small>每日{{ usageMetricLabel() }}</small>
          </div>
          <div ref="memoryCircleUsageChart" class="usage-chart"></div>
        </div>

        <div class="usage-group">
          <div class="usage-group-title">
            <span>对话抽取</span>
            <small>每日{{ usageMetricLabel() }}</small>
          </div>
          <div ref="conversationUsageChart" class="usage-chart"></div>
        </div>

        <div class="usage-group usage-group-total">
          <div class="usage-group-title">
            <span>记忆圈 + 对话抽取</span>
            <small>每日{{ usageMetricLabel() }}</small>
          </div>
          <div ref="totalUsageChart" class="usage-chart"></div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import request from '@/utils/request';

function emptyMemoryCircleUsage() {
  return {
    userPostQuotas: [],
    userPostInputTokens: [],
    userPostOutputTokens: [],
    npcPostQuotas: [],
    npcPostInputTokens: [],
    npcPostOutputTokens: [],
    npcCommentQuotas: [],
    npcCommentInputTokens: [],
    npcCommentOutputTokens: [],
    checkQuotas: [],
    checkInputTokens: [],
    checkOutputTokens: [],
    totalQuotas: [],
    totalInputTokens: [],
    totalOutputTokens: [],
    totalQuota: 0,
    inputTokenTotal: 0,
    outputTokenTotal: 0
  };
}

function emptyConversationUsage() {
  return {
    chatMemoryQuotas: [],
    chatMemoryInputTokens: [],
    chatMemoryOutputTokens: [],
    importMemoryQuotas: [],
    importMemoryInputTokens: [],
    importMemoryOutputTokens: [],
    totalQuotas: [],
    totalInputTokens: [],
    totalOutputTokens: [],
    totalQuota: 0,
    inputTokenTotal: 0,
    outputTokenTotal: 0
  };
}

function emptyUsageStatistics() {
  return {
    startDate: '',
    endDate: '',
    dates: [],
    memoryCircle: emptyMemoryCircleUsage(),
    conversation: emptyConversationUsage(),
    totalQuotas: [],
    totalInputTokens: [],
    totalOutputTokens: [],
    totalQuota: 0,
    inputTokenTotal: 0,
    outputTokenTotal: 0
  };
}

var _this;
export default {
  name: 'Dashboard',
  data() {
    return {
      activeTab: '7',
      chart: null,
      stats: {
        totalUsers: 0,
        newUsers: 0
      },
      trendData: {
        dates: [],
        visitCounts: [],
        activateCounts: []
      },
      usageLoading: false,
      usageMetric: 'quota',
      usageQuery: {
        dateRange: []
      },
      usageCharts: {
        memoryCircle: null,
        conversation: null,
        total: null
      },
      usageStatistics: emptyUsageStatistics()
    };
  },
  mounted() {
    _this = this;
    _this.setDefaultUsageDateRange();
    _this.$nextTick(() => {
      _this.initChart();
      _this.initUsageCharts();
      window.addEventListener('resize', _this.resizeChart);
      _this.queryOverview();
      _this.queryTrend();
      _this.queryUsage();
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', _this.resizeChart);
    if (_this.chart) {
      _this.chart.dispose();
    }
    Object.keys(_this.usageCharts).forEach(key => {
      if (_this.usageCharts[key]) {
        _this.usageCharts[key].dispose();
      }
    });
  },
  methods: {
    queryOverview() {
      request({
        url: '/user/statistics/overview',
        method: 'post'
      }).then(res => {
        _this.stats.totalUsers = res.totalUserCount || 0;
        _this.stats.newUsers = res.yesterdayNewUserCount || 0;
      });
    },
    queryTrend() {
      request({
        url: '/user/statistics/trend',
        method: 'post',
        data: { days: Number(_this.activeTab) }
      }).then(res => {
        _this.trendData.dates = (res.dates || []).map(_this.formatStatisticsDate);
        _this.trendData.visitCounts = res.visitCounts || [];
        _this.trendData.activateCounts = res.activateCounts || [];
        _this.updateChart();
      });
    },
    setDefaultUsageDateRange() {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 6);
      _this.usageQuery.dateRange = [_this.formatDate(startDate), _this.formatDate(endDate)];
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatStatisticsDate(value) {
      const text = String(value || '');
      const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(text);
      return compact ? `${compact[1]}-${compact[2]}-${compact[3]}` : text;
    },
    queryUsage() {
      if (!_this.usageQuery.dateRange || _this.usageQuery.dateRange.length !== 2) {
        _this.$message.warning('请选择统计时间');
        return Promise.resolve();
      }
      const startTime = new Date(`${_this.usageQuery.dateRange[0]}T00:00:00`).getTime();
      const endTime = new Date(`${_this.usageQuery.dateRange[1]}T00:00:00`).getTime();
      const dateRangeDays = Math.round((endTime - startTime) / 86400000) + 1;
      if (dateRangeDays > 31) {
        _this.$message.warning('统计日期范围不能超过 31 天');
        return Promise.resolve();
      }
      _this.usageLoading = true;
      return request({
        url: '/user/statistics/usage',
        method: 'post',
        data: {
          startDate: _this.usageQuery.dateRange[0],
          endDate: _this.usageQuery.dateRange[1]
        }
      }).then(res => {
        const defaults = emptyUsageStatistics();
        _this.usageStatistics = {
          startDate: res.startDate || _this.usageQuery.dateRange[0],
          endDate: res.endDate || _this.usageQuery.dateRange[1],
          dates: res.dates || [],
          memoryCircle: Object.assign(defaults.memoryCircle, res.memoryCircle || {}),
          conversation: Object.assign(defaults.conversation, res.conversation || {}),
          totalQuotas: res.totalQuotas || [],
          totalInputTokens: res.totalInputTokens || [],
          totalOutputTokens: res.totalOutputTokens || [],
          totalQuota: Number(res.totalQuota || 0),
          inputTokenTotal: Number(res.inputTokenTotal || 0),
          outputTokenTotal: Number(res.outputTokenTotal || 0)
        };
        _this.renderUsageCharts();
      }).finally(() => {
        _this.usageLoading = false;
      });
    },
    quotaToPrice(value) {
      const quota = Number(value || 0);
      const exchangeRate = Number(window.g && window.g.usdCnyRate);
      return Number.isFinite(quota) && Number.isFinite(exchangeRate) && exchangeRate > 0
        ? quota / 500000 * exchangeRate : NaN;
    },
    formatQuotaPrice(value) {
      return this.formatPrice(this.quotaToPrice(value));
    },
    formatPrice(value) {
      const price = Number(value);
      return Number.isFinite(price) ? `${price.toFixed(6)} 元` : '汇率未配置';
    },
    formatToken(value) {
      const token = Number(value || 0);
      return Number.isFinite(token) ? token.toLocaleString('zh-CN') : '0';
    },
    initChart() {
      _this.chart = echarts.init(_this.$refs.chart);
    },
    initUsageCharts() {
      if (_this.$refs.memoryCircleUsageChart) {
        _this.usageCharts.memoryCircle = echarts.init(_this.$refs.memoryCircleUsageChart);
        _this.usageCharts.conversation = echarts.init(_this.$refs.conversationUsageChart);
        _this.usageCharts.total = echarts.init(_this.$refs.totalUsageChart);
      }
    },
    usageMetricLabel() {
      return {
        quota: '总 Token',
        input: '输入 Token',
        output: '输出 Token',
        price: '价格'
      }[_this.usageMetric] || '总 Token';
    },
    usageSeriesData(quotas, inputTokens, outputTokens) {
      const length = Math.max(
        (quotas || []).length,
        (inputTokens || []).length,
        (outputTokens || []).length
      );
      return Array.from({length}, (unused, index) => {
        const quota = Number((quotas || [])[index] || 0);
        const input = Number((inputTokens || [])[index] || 0);
        const output = Number((outputTokens || [])[index] || 0);
        const price = _this.quotaToPrice(quota);
        const values = {quota, input, output, price};
        return {
          value: values[_this.usageMetric],
          quota,
          inputTokens: input,
          outputTokens: output,
          price
        };
      });
    },
    usageChartOption(series) {
      return {
        color: ['#2878b5', '#4ca58b', '#e0a12f', '#d45d5d', '#6d63b5'],
        tooltip: {
          trigger: 'axis',
          confine: true,
          axisPointer: {type: 'shadow'},
          formatter(params) {
            if (!params || params.length === 0) {
              return '';
            }
            const rows = params.map(item => {
              const data = item.data || {};
              const isTotal = item.seriesName.includes('总使用');
              const rowStyle = isTotal
                ? 'border-top:1px solid #dcdfe6;font-weight:600;'
                : '';
              return `<tr style="${rowStyle}">`
                + `<td style="padding:6px 16px 6px 0;white-space:nowrap;">`
                + `${item.marker || ''}${item.seriesName}</td>`
                + `<td style="padding:6px 12px;text-align:right;white-space:nowrap;">`
                + `${_this.formatToken(data.inputTokens)}</td>`
                + `<td style="padding:6px 12px;text-align:right;white-space:nowrap;">`
                + `${_this.formatToken(data.outputTokens)}</td>`
                + `<td style="padding:6px 12px;text-align:right;white-space:nowrap;">`
                + `${_this.formatToken(data.quota)}</td>`
                + `<td style="padding:6px 0 6px 12px;text-align:right;white-space:nowrap;">`
                + `${_this.formatQuotaPrice(data.quota)}</td>`
                + '</tr>';
            }).join('');
            return `<div style="max-width:calc(100vw - 48px);overflow-x:auto;">`
              + `<div style="margin-bottom:6px;font-weight:600;">${params[0].axisValue}</div>`
              + '<table style="border-collapse:collapse;font-variant-numeric:tabular-nums;">'
              + '<thead><tr style="color:#909399;border-bottom:1px solid #ebeef5;">'
              + '<th style="padding:4px 16px 6px 0;text-align:left;white-space:nowrap;">类型</th>'
              + '<th style="padding:4px 12px 6px;text-align:right;white-space:nowrap;">输入</th>'
              + '<th style="padding:4px 12px 6px;text-align:right;white-space:nowrap;">输出</th>'
              + '<th style="padding:4px 12px 6px;text-align:right;white-space:nowrap;">总 Token</th>'
              + '<th style="padding:4px 0 6px 12px;text-align:right;white-space:nowrap;">价格</th>'
              + `</tr></thead><tbody>${rows}</tbody></table></div>`;
          }
        },
        legend: {top: 0, type: 'scroll'},
        grid: {left: 20, right: 20, top: 54, bottom: 58, containLabel: true},
        xAxis: {
          type: 'category',
          data: _this.usageStatistics.dates,
          axisLabel: {rotate: _this.usageStatistics.dates.length > 10 ? 35 : 0}
        },
        yAxis: {
          type: 'value',
          name: _this.usageMetricLabel(),
          axisLabel: {
            formatter(value) {
              return _this.usageMetric === 'price' ? `${value} 元` : _this.formatToken(value);
            }
          }
        },
        series
      };
    },
    usageBarSeries(name, quotas, inputTokens, outputTokens, stack) {
      return {
        name,
        type: 'bar',
        stack,
        barMaxWidth: 38,
        data: _this.usageSeriesData(quotas, inputTokens, outputTokens)
      };
    },
    usageTotalSeries(name, quotas, inputTokens, outputTokens) {
      return {
        name,
        type: 'line',
        symbolSize: 7,
        z: 3,
        data: _this.usageSeriesData(quotas, inputTokens, outputTokens)
      };
    },
    renderUsageCharts() {
      if (!_this.usageCharts.memoryCircle) {
        return;
      }
      const memoryCircle = _this.usageStatistics.memoryCircle;
      const conversation = _this.usageStatistics.conversation;
      _this.usageCharts.memoryCircle.setOption(_this.usageChartOption([
        _this.usageBarSeries('发帖使用量', memoryCircle.userPostQuotas,
          memoryCircle.userPostInputTokens, memoryCircle.userPostOutputTokens, 'memoryCircle'),
        _this.usageBarSeries('NPC 发帖使用量', memoryCircle.npcPostQuotas,
          memoryCircle.npcPostInputTokens, memoryCircle.npcPostOutputTokens, 'memoryCircle'),
        _this.usageBarSeries('NPC 评论使用量', memoryCircle.npcCommentQuotas,
          memoryCircle.npcCommentInputTokens, memoryCircle.npcCommentOutputTokens, 'memoryCircle'),
        _this.usageBarSeries('审核使用量', memoryCircle.checkQuotas,
          memoryCircle.checkInputTokens, memoryCircle.checkOutputTokens, 'memoryCircle'),
        _this.usageTotalSeries('Token 总使用', memoryCircle.totalQuotas,
          memoryCircle.totalInputTokens, memoryCircle.totalOutputTokens)
      ]), true);
      _this.usageCharts.conversation.setOption(_this.usageChartOption([
        _this.usageBarSeries('聊天记忆', conversation.chatMemoryQuotas,
          conversation.chatMemoryInputTokens, conversation.chatMemoryOutputTokens, 'conversation'),
        _this.usageBarSeries('导入记忆', conversation.importMemoryQuotas,
          conversation.importMemoryInputTokens, conversation.importMemoryOutputTokens, 'conversation'),
        _this.usageTotalSeries('对话板块总使用', conversation.totalQuotas,
          conversation.totalInputTokens, conversation.totalOutputTokens)
      ]), true);
      _this.usageCharts.total.setOption(_this.usageChartOption([
        _this.usageBarSeries('记忆圈', memoryCircle.totalQuotas,
          memoryCircle.totalInputTokens, memoryCircle.totalOutputTokens, 'total'),
        _this.usageBarSeries('对话抽取', conversation.totalQuotas,
          conversation.totalInputTokens, conversation.totalOutputTokens, 'total'),
        _this.usageTotalSeries('全部总使用', _this.usageStatistics.totalQuotas,
          _this.usageStatistics.totalInputTokens, _this.usageStatistics.totalOutputTokens)
      ]), true);
    },
    updateChart() {
      var option = {
        tooltip: {
          trigger: 'axis'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: _this.trendData.dates,
          axisLabel: {
            color: '#999'
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            color: '#999'
          }
        },
        series: [
          {
            name: '访问量',
            type: 'line',
            smooth: true,
            data: _this.trendData.visitCounts,
            lineStyle: {
              color: '#4ea1f3'
            },
            itemStyle: {
              color: '#4ea1f3'
            }
          },
          {
            name: '激活量',
            type: 'line',
            smooth: true,
            data: _this.trendData.activateCounts,
            lineStyle: {
              color: '#f5a623'
            },
            itemStyle: {
              color: '#f5a623'
            }
          }
        ]
      };
      _this.chart.setOption(option, true);
    },
    switchTab(tab) {
      _this.activeTab = tab;
      _this.queryTrend();
    },
    resizeChart() {
      if (_this.chart) {
        _this.chart.resize();
      }
      Object.keys(_this.usageCharts).forEach(key => {
        if (_this.usageCharts[key]) {
          _this.usageCharts[key].resize();
        }
      });
    },
    goUserManage() {
      _this.$router.push({ path: '/userManage' });
    }
  }
}
</script>

<style scoped lang="scss">
.dashboard-content {
  padding: 20px;
  height: calc(100vh - 120px);
  overflow: auto;
  box-sizing: border-box;
}

.dashboard-panel {
  display: flex;
  gap: 20px;
  min-height: 380px;
}

.status-card {
  flex: 0 0 320px;
  background: #fff;
  border-radius: 8px;
  padding: 30px;
  /*
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  */
  display: flex;
  flex-direction: column;

  .card-title {
    font-size: 18px;
    font-weight: bold;
    color: #666;
    margin-bottom: 30px;
  }

  .card-row {
    margin-bottom: 15px;

    .card-label {
      font-size: 16px;
      color: #666;
    }

    .card-number {
      font-size: 40px;
      font-weight: bold;
      color: #4ea1f3;
      margin: 0 5px;
    }

    .card-number.highlight {
      color: #f5a623;
    }
  }

  .manage-btn {
    margin-top: 30px;
    width: 120px;
    border-color: #dcdfe6;
    color: #606266;
  }
}

.chart-card {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  /*
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  */
  display: flex;
  flex-direction: column;

  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }

  .chart-tabs {
    display: flex;
    gap: 20px;

    .tab-item {
      font-size: 14px;
      color: #999;
      cursor: pointer;
      padding-bottom: 5px;

      &.active {
        color: #4ea1f3;
        font-weight: bold;
        border-bottom: 2px solid #4ea1f3;
      }
    }
  }

  .chart-legend {
    display: flex;
    align-items: center;
    gap: 15px;

    .legend-item {
      display: flex;
      align-items: center;
      font-size: 13px;
      color: #999;
    }

    .legend-dot {
      width: 20px;
      height: 3px;
      margin-right: 5px;
      border-radius: 2px;

      &.visit {
        background: #4ea1f3;
      }

      &.activate {
        background: #f5a623;
      }
    }

    .legend-badge {
      margin-left: 10px;
    }
  }

  .chart-container {
    flex: 1;
    min-height: 300px;
  }
}

.usage-section {
  margin-top: 20px;
  padding: 24px;
  background: #fff;
  border-radius: 8px;
}

.usage-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #ebeef5;

  h2 {
    margin: 0;
    color: #303133;
    font-size: 18px;
    letter-spacing: 0;
  }

  p {
    margin: 8px 0 0;
    color: #909399;
    font-size: 13px;
  }
}

.usage-filter {
  display: flex;
  align-items: center;
  gap: 10px;
}

.usage-metric-switch {
  display: flex;
  flex-wrap: wrap;
}

.usage-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-bottom: 1px solid #ebeef5;
  background: #f7f9fb;
}

.usage-summary-item {
  min-width: 0;
  padding: 18px 20px;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
  gap: 6px;

  &:last-child {
    border-right: 0;
  }

  span {
    color: #606266;
    font-size: 13px;
  }

  strong {
    color: #2878b5;
    font-size: 22px;
    line-height: 28px;
    overflow-wrap: anywhere;
  }

  small {
    color: #909399;
    font-size: 12px;
    overflow-wrap: anywhere;
  }

  &.overall strong {
    color: #d48806;
  }
}

.usage-group {
  padding: 22px 0;
  border-bottom: 1px solid #ebeef5;
}

.usage-group-total {
  border-bottom: 0;
  padding-bottom: 0;
}

.usage-group-title {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 14px;
  color: #303133;
  font-size: 16px;
  font-weight: 600;

  small {
    color: #909399;
    font-size: 12px;
    font-weight: 400;
  }
}

.usage-chart {
  width: 100%;
  height: 340px;
}

@media (max-width: 900px) {
  .dashboard-panel,
  .usage-header {
    flex-direction: column;
  }

  .status-card {
    flex-basis: auto;
    width: 100%;
    box-sizing: border-box;
  }

  .chart-card {
    width: 100%;
    min-height: 360px;
    box-sizing: border-box;
  }

  .usage-filter {
    width: 100%;
    flex-wrap: wrap;
  }

  .usage-summary {
    grid-template-columns: minmax(0, 1fr);
  }

  .usage-summary-item {
    border-right: 0;
    border-bottom: 1px solid #e4e7ed;

    &:last-child {
      border-bottom: 0;
    }
  }
}

@media (max-width: 560px) {
  .dashboard-content,
  .usage-section {
    padding: 14px;
  }

  .usage-chart {
    height: 300px;
  }
}
</style>
